import type { SubscriptionWarning } from '@/hooks/useSubscriptionWarning';

export const HARDSHIP_EMAIL = 'hello@worklynx.com.au';

export interface SubscriptionWarningCopy {
  title: string;
  body: string;
  /** Shown only once access has actually lapsed. */
  hardship: string | null;
  actionLabel: string;
}

function remainingLabel(daysRemaining: number | null): string {
  if (daysRemaining === null) return 'soon';
  if (daysRemaining <= 0) return 'today';
  if (daysRemaining === 1) return 'tomorrow';
  return `in ${daysRemaining} days`;
}

/**
 * What to say, which depends on both how close the deadline is and what kind of deadline
 * it is. A lapsed trial and a lapsed subscription lose different things, so telling both
 * the same story would mislead one of them.
 */
export function getSubscriptionWarningCopy(
  warning: SubscriptionWarning,
): SubscriptionWarningCopy | null {
  const { level, kind, daysRemaining } = warning;
  if (level === 'NONE') return null;

  if (kind === 'TRIAL') {
    if (level === 'LAPSED') {
      return {
        title: 'Your trial has ended',
        body: 'Approvals, reports and your data export stay open for 14 days so pay for work already done can still be approved. Everything else has stopped. Subscribe to bring the workspace back.',
        hardship: null,
        actionLabel: 'Choose a plan',
      };
    }
    return {
      title: `Your trial ends ${remainingLabel(daysRemaining)}`,
      body: 'Subscribe before then to keep rostering, approvals and reporting. Without a subscription the workspace stops and only approvals, reports and export remain, for 14 days.',
      hardship: null,
      actionLabel: 'Choose a plan',
    };
  }

  if (level === 'LAPSED') {
    return {
      title: 'Your subscription has lapsed',
      body: 'Rostering, pay and leave approvals and reports keep running, so your staff are not caught in the middle. Adding clients, jobs and staff is paused. Access ends 90 days after the lapse.',
      hardship: `If you are having financial difficulty, email ${HARDSHIP_EMAIL} with your organisation name, ABN and what you are facing, and we will work something out.`,
      actionLabel: 'Renew subscription',
    };
  }

  return {
    title: `Your subscription renews ${remainingLabel(daysRemaining)}`,
    body: 'Make sure payment reaches us before then. If it does not, adding clients, jobs and staff pauses, though rostering and approvals keep running.',
    hardship: null,
    actionLabel: 'View billing',
  };
}
